import { ReactNode } from "react";
import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { ContextStrip } from "@pages/AdminDashboard/ui/ContextStrip";
import { ExternalLink } from "@pages/AdminDashboard/ui/DataTable/cells";
import { FieldList } from "@pages/AdminDashboard/ui/FieldList";
import { PageHeader } from "@pages/AdminDashboard/ui/PageHeader";
import { Tag } from "@pages/AdminDashboard/ui/StatusBadge";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

const [spaceXs, , spaceMd] = adminTokens.spacing;

const Example = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  gap: ${spaceXs}px;
  margin-bottom: ${spaceMd}px;
  border: 1px dashed ${adminTokens.color.border};
  border-radius: ${adminTokens.radius.row}px;
  padding: ${spaceMd}px;
`;

const Caption = styled.span`
  color: ${adminTokens.color.inkSoft};
  font-family: ${adminTokens.typeScale.meta.fontFamily};
  font-size: ${adminTokens.typeScale.meta.fontSize};
`;

const Labelled = ({ name, children }: { name: string; children: ReactNode }) => (
  <Example aria-label={name}>
    <Caption>{name}</Caption>
    {children}
  </Example>
);

const shortName = "Short example";
const longName = "Long content example";

const longIdentifier = "3f9a1c5e7b2d4086a1b3c5d7e9f0a2b4c6d8e0f1a3b5c7d9e1f2a4b6c8d0e2f4";
const longParagraph =
  "A long body that runs past one line so the card shows how its content wraps inside the padding and never pushes the card wider than its column.";

export const CardSection = () => (
  <UiKitSection title="Card">
    <Labelled name={shortName}>
      <Card
        eyebrow="Selected Order"
        title="OID c3d8-test-0001"
        titleLevel={3}
        aside={<Tag>Current</Tag>}
      >
        Shipping method standard
      </Card>
    </Labelled>
    <Labelled name={longName}>
      <Card
        eyebrow="Selected Order"
        title="OID c3d8-test-0001-with-a-very-long-identifier-that-wraps-inside-the-card-header"
        titleLevel={3}
        aside={<Tag>Reship</Tag>}
      >
        {longParagraph}
      </Card>
    </Labelled>
  </UiKitSection>
);

export const PageHeaderSection = () => (
  <UiKitSection title="Page header">
    <Labelled name={shortName}>
      <PageHeader
        title="Redo"
        titleLevel={3}
        subtitle="Cancel or replace an Order. The User is never charged again."
        aside={<Button look="primary">Find Order</Button>}
      />
    </Labelled>
    <Labelled name={longName}>
      <PageHeader
        title="Cancel or replace an Order for a patient with a very long case title"
        titleLevel={3}
        subtitle={longParagraph}
        aside={<Button look="primary">Find Order</Button>}
      />
    </Labelled>
  </UiKitSection>
);

export const FieldListSection = () => (
  <UiKitSection title="Field list">
    <Labelled name={shortName}>
      <FieldList
        fields={[
          { label: "Shipping method", value: "standard" },
          { label: "Tracking", value: "Not shipped yet" },
          {
            label: "Invoice",
            value: (
              <ExternalLink href="https://dashboard.stripe.com/invoices/in_TEST1a2b3c">
                in_TEST1a2b3c
              </ExternalLink>
            ),
          },
          { label: "Ships to", value: "100 Test Street, Apt 4B, Miami, FL 33101" },
        ]}
      />
    </Labelled>
    <Labelled name={longName}>
      <FieldList
        fields={[
          { label: "Patient id", value: longIdentifier },
          { label: "Payment customer id", value: "cus_TEST1234567890abcdefghij" },
          { label: "Ships to", value: `${longParagraph} 100 Test Street, Miami, FL 33101` },
          { label: "Email", value: "a.very.long.test.traveler.email.address@example.com" },
        ]}
      />
    </Labelled>
  </UiKitSection>
);

export const ContextStripSection = () => (
  <UiKitSection title="Context strip">
    <Labelled name={shortName}>
      <ContextStrip
        label="Case context, short"
        identifiers={[
          "Case #10482",
          "Test Traveler · test.traveler@example.com",
          "Peru · Oct 12 – Oct 26, 2026",
          "Invoice in_TEST1a2b3c · paid",
        ]}
      />
    </Labelled>
    <Labelled name={longName}>
      <ContextStrip
        label="Case context, long"
        identifiers={[
          "Case #10482-with-an-extra-long-reference",
          "Test Traveler With A Very Long Name · a.very.long.test.traveler.email.address@example.com",
          "Peru, Cusco and the Sacred Valley · Oct 12 – Oct 26, 2026 · 14 days",
          "Invoice in_TEST1a2b3c4d5e6f7g8h9i0j · paid",
        ]}
      />
    </Labelled>
  </UiKitSection>
);
